/** Every JSON object in a body, whether it arrived as one document or as streamed events. */
export function jsonObjects(body: string): Record<string, unknown>[] {
  const values: Record<string, unknown>[] = [];
  const candidates = body
    .split("\n")
    .map(line => line.trim())
    .filter(line => line.startsWith("data:"))
    .map(line => line.slice("data:".length).trim())
    .filter(line => line && line !== "[DONE]");
  if (candidates.length === 0 && body.trim()) candidates.push(body.trim());

  for (const candidate of candidates) {
    try {
      const value = JSON.parse(candidate) as unknown;
      if (isRecord(value)) values.push(value);
    } catch {}
  }
  return values;
}

/** What the provider charged OpenRouter, which reports it beside its own price in `usage`. */
export function upstreamCost(usage: Record<string, unknown>): number | undefined {
  return isRecord(usage.cost_details)
    ? finiteNumber(usage.cost_details.upstream_inference_cost)
    : undefined;
}

/** The body as one JSON object, or an empty one when it is not. */
export function jsonObject(body: string): Record<string, unknown> {
  try {
    const value = JSON.parse(body) as unknown;
    return isRecord(value) ? value : {};
  } catch {
    return {};
  }
}

export function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

export function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

export function lastMatching<T>(items: readonly T[], matches: (item: T) => boolean): T | undefined {
  for (let index = items.length - 1; index >= 0; index--) {
    const item = items[index] as T;
    if (matches(item)) return item;
  }
}

/** A message's content as text: a string as is, or its parts joined, naming parts with no text. */
export function contentText(content: unknown): string {
  if (typeof content === "string") return content;
  return records(content)
    .map(
      part =>
        nonEmptyString(part.text) ??
        nonEmptyString(part.refusal) ??
        (part.type ? `[${String(part.type)}]` : ""),
    )
    .join("");
}

/** Reasoning summaries, then reasoning content, one paragraph each. */
export function reasoningText(item: Record<string, unknown>): string {
  return [...records(item.summary), ...records(item.content)]
    .map(part => nonEmptyString(part.text) ?? "")
    .filter(Boolean)
    .join("\n\n");
}

/** Tool call arguments as the provider streamed them: a JSON string, or already an object. */
export function argumentsText(value: unknown): string {
  if (value === undefined) return "";
  return typeof value === "string" ? value : JSON.stringify(value);
}

export function parseArguments(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/**
 * A value laid out for people to read: JSON text is parsed and indented, and strings show their
 * line breaks, tabs and quotes as they would print instead of as `\n`, `\t` and `\"` escapes.
 * Strings that span lines sit between `"""` fences, indented under their key.
 */
export function readableText(value: unknown): string {
  const parsed = typeof value === "string" ? parseJsonText(value) : value;
  return typeof parsed === "string" ? unescapedText(parsed) : readableJson(parsed, "");
}

function parseJsonText(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return text;
  try {
    return JSON.parse(trimmed);
  } catch {
    return text;
  }
}

const ESCAPED_CHARACTER = /\\(["\\/nrt])/g;
const PRINTED_CHARACTER: Record<string, string> = { n: "\n", r: "", t: "\t" };

function unescapedText(text: string): string {
  if (!/\\[nt]/.test(text)) return text;
  return text.replace(
    ESCAPED_CHARACTER,
    (_, escaped: string) => PRINTED_CHARACTER[escaped] ?? escaped,
  );
}

function readableJson(value: unknown, indent: string): string {
  const inner = `${indent}  `;
  if (typeof value === "string") {
    const text = unescapedText(value);
    if (!text.includes("\n")) return JSON.stringify(text);
    const lines = text.split("\n").map(line => (line ? `${inner}${line}` : ""));
    return `"""\n${lines.join("\n")}\n${inner}"""`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    const items = value.map(item => `${inner}${readableJson(item, inner)}`);
    return `[\n${items.join(",\n")}\n${indent}]`;
  }
  if (isRecord(value)) {
    const entries = Object.entries(value).filter(([, entry]) => entry !== undefined);
    if (entries.length === 0) return "{}";
    const fields = entries.map(
      ([key, entry]) => `${inner}${JSON.stringify(key)}: ${readableJson(entry, inner)}`,
    );
    return `{\n${fields.join(",\n")}\n${indent}}`;
  }
  return JSON.stringify(value) ?? String(value);
}

export function errorCode(error: Record<string, unknown>): string | undefined {
  return error.code === undefined || error.code === null
    ? nonEmptyString(error.type)
    : String(error.code);
}

export function outputType(format: string | undefined): string | undefined {
  if (format === "json_object" || format === "json_schema") return "json";
  return format === "text" ? "text" : undefined;
}
